import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import SectionHeading from '@/components/SectionHeading';
import { useInView } from '@/lib/hooks';
import { PORTFOLIO } from '@/constants/testIds';

const PROJECTS = [
  { name: 'The Brew Spot',    type: 'Café',      tag: 'Restaurant', img: 'https://images.unsplash.com/photo-1501339847302-ac426a4a7cbb?w=1100&q=80', offset: 'md:translate-y-0' },
  { name: 'Luxe Threads',     type: 'Retail',    tag: 'E-commerce', img: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1100&q=80', offset: 'md:translate-y-8' },
  { name: 'Aryan Interiors',  type: 'Interiors', tag: 'Portfolio',  img: 'https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?w=1100&q=80', offset: 'md:translate-y-0' },
];

function HexBadge({ label }) {
  return (
    <div className="absolute top-4 left-4 z-10">
      <svg width="72" height="78" viewBox="0 0 72 78">
        <polygon points="36,4 68,22 68,56 36,74 4,56 4,22" fill="#FFFFFF" stroke="#F5DFAE" strokeWidth="1" />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center font-accent font-semibold text-[11px] uppercase tracking-widest text-[color:var(--sh-amber-2)]">
        {label}
      </span>
    </div>
  );
}

function Card({ p, i, inView }) {
  return (
    <motion.div
      data-testid={PORTFOLIO.card(i)}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: 0.1 + i * 0.1 }}
      className={`portfolio-card ${p.offset}`}
    >
      <HexBadge label={p.type} />
      <div className="aspect-[4/3] overflow-hidden relative">
        <img src={p.img} alt={p.name} loading="lazy" className="w-full h-full object-cover" />
        <div className="portfolio-overlay" />
        <div className="portfolio-cta">
          <span className="btn-amber inline-flex items-center gap-2 text-sm">
            View Project <ArrowUpRight size={14} />
          </span>
        </div>
      </div>
      <div className="px-5 py-4 flex items-center justify-between">
        <div>
          <div className="font-display text-base md:text-lg font-semibold text-[color:var(--sh-ink)]">{p.name}</div>
          <div className="text-[11px] font-accent uppercase tracking-widest text-[color:var(--sh-muted)] mt-1">{p.tag}</div>
        </div>
        <span className="tag-pill">{p.tag}</span>
      </div>
    </motion.div>
  );
}

export default function Portfolio() {
  const [ref, inView] = useInView({ threshold: 0.15, once: true });
  return (
    <section id="work" className="section" data-testid={PORTFOLIO.block}>
      <div className="container-pro">
        <SectionHeading
          eyebrow="SELECTED WORK"
          title="Sites that don’t just sit there."
          subtitle="A growing portfolio of real projects for real businesses."
        />
        <div ref={ref} className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PROJECTS.map((p, i) => <Card key={p.name} p={p} i={i} inView={inView} />)}
        </div>
        <div className="text-center mt-12">
          <button
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="font-display text-base md:text-lg text-[color:var(--sh-amber-2)] inline-flex items-center gap-2 hover:opacity-80 italic"
          >
            Your project could be next. <ArrowUpRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
}
